import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { WHATSAPP } from "@/lib/itv/site";

const campo =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";

type Estado = {
  id: string | null;
  valor: string;
};

function useSetting(chave: string, padrao: string) {
  const [estado, setEstado] = useState<Estado>({ id: null, valor: padrao });
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    void supabase
      .from("settings")
      .select("id, value")
      .eq("key", chave)
      .maybeSingle()
      .then(({ data }) => {
        setEstado({ id: data?.id ?? null, valor: data?.value ?? padrao });
        setCarregando(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  return { estado, setEstado, carregando };
}

async function salvarSetting(
  chave: string,
  estado: Estado,
  valor: string,
): Promise<string | null> {
  if (estado.id) {
    const { error } = await supabase
      .from("settings")
      .update({ value: valor, updated_at: new Date().toISOString() })
      .eq("id", estado.id);
    if (error) return error.message;
    return null;
  }
  const { error } = await supabase.from("settings").insert({ key: chave, value: valor });
  if (error) return error.message;
  return null;
}

export const Route = createFileRoute("/admin/configuracoes")({
  ssr: false,
  head: () => ({
    meta: [{ title: "Configurações | Painel" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: Configuracoes,
});

function Configuracoes() {
  const agendamento = useSetting("appointment_url", WHATSAPP);
  const [linkAgendamento, setLinkAgendamento] = useState("");
  const [mensagemAgendamento, setMensagemAgendamento] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setLinkAgendamento(agendamento.estado.valor);
  }, [agendamento.estado.valor]);

  async function salvarAgendamento() {
    const valor = linkAgendamento.trim();
    try {
      const url = new URL(valor);
      if (url.protocol !== "https:") throw new Error();
    } catch {
      setMensagemAgendamento("Informe um endereço completo começando com https://");
      return;
    }

    setSalvando(true);
    setMensagemAgendamento(null);
    const erro = await salvarSetting("appointment_url", agendamento.estado, valor);
    setSalvando(false);
    if (erro) {
      setMensagemAgendamento(`Não foi possível salvar: ${erro}`);
      return;
    }
    if (!agendamento.estado.id) agendamento.setEstado({ id: null, valor });
    setMensagemAgendamento("Salvo. O novo link já será usado em todos os agendamentos.");
  }

  if (agendamento.carregando) {
    return <p className="text-sm text-muted-foreground">Carregando...</p>;
  }

  return (
    <section className="max-w-2xl">
      <h1 className="text-lg font-semibold text-foreground">Configurações</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Endereços e integrações usados em todo o site.
      </p>
      <form
        className="mt-6 space-y-4 rounded-md border border-border p-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          void salvarAgendamento();
        }}
      >
        {mensagemAgendamento ? (
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">{mensagemAgendamento}</p>
        ) : null}
        <div>
          <label className="block text-sm font-medium text-foreground" htmlFor="link-agendamento">
            Link de agendamento pelo WhatsApp
          </label>
          <p className="mt-1 text-sm text-muted-foreground">
            Usado em todos os botões de agendamento e no botão flutuante do WhatsApp.
          </p>
          <input
            id="link-agendamento"
            type="url"
            required
            className={`${campo} mt-2`}
            value={linkAgendamento}
            onChange={(evento) => setLinkAgendamento(evento.target.value)}
          />
        </div>
        <Button type="submit" disabled={salvando}>
          {salvando ? "Salvando..." : "Salvar link"}
        </Button>
      </form>
    </section>
  );
}
